/**
 * Текстовый слой поиска (порт search-engine.ts из референса
 * advanced-3d-territory-mapping (7)): нормализация, транслитерация в обе
 * стороны, исправление забытой раскладки QWERTY↔ЙЦУКЕН, опечатки
 * (Дамерау-Левенштейн с потолком), скоринг ключевого слова.
 */

export function normText(value: string): string {
  return value
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[«»"'`.,/\\()[\]{}:;!?—–-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Латиница → кириллица фонетически («cdek», «kohvik» находят кириллицу). */
const LAT_TO_CYR: readonly (readonly [string, string])[] = [
  ['shch', 'щ'],
  ['sch', 'щ'],
  ['yo', 'ё'],
  ['zh', 'ж'],
  ['kh', 'х'],
  ['ts', 'ц'],
  ['ch', 'ч'],
  ['sh', 'ш'],
  ['yu', 'ю'],
  ['ya', 'я'],
  ['ye', 'е'],
  ['a', 'а'],
  ['b', 'б'],
  ['c', 'к'],
  ['d', 'д'],
  ['e', 'е'],
  ['f', 'ф'],
  ['g', 'г'],
  ['h', 'х'],
  ['i', 'и'],
  ['j', 'й'],
  ['k', 'к'],
  ['l', 'л'],
  ['m', 'м'],
  ['n', 'н'],
  ['o', 'о'],
  ['p', 'п'],
  ['q', 'к'],
  ['r', 'р'],
  ['s', 'с'],
  ['t', 'т'],
  ['u', 'у'],
  ['v', 'в'],
  ['w', 'в'],
  ['x', 'кс'],
  ['y', 'ы'],
  ['z', 'з'],
]

export function latinToCyrillic(value: string): string {
  let out = value.toLowerCase()
  for (const [lat, cyr] of LAT_TO_CYR) out = out.split(lat).join(cyr)
  return out
}

const CYR_TO_LAT: Readonly<Record<string, string>> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
}

export function cyrillicToLatin(value: string): string {
  return value
    .toLowerCase()
    .split('')
    .map((ch) => CYR_TO_LAT[ch] ?? ch)
    .join('')
}

/** Физические клавиши QWERTY↔ЙЦУКЕН: пользователь забыл переключить раскладку. */
const QWERTY = "qwertyuiop[]asdfghjkl;'zxcvbnm,."
const JCUKEN = 'йцукенгшщзхъфывапролджэячсмитьбю'

export function fixKeyboardLayout(value: string): string {
  let converted = ''
  let changed = false
  for (const ch of value.toLowerCase()) {
    const qi = QWERTY.indexOf(ch)
    if (qi >= 0) {
      converted += JCUKEN[qi]
      changed = true
      continue
    }
    const ji = JCUKEN.indexOf(ch)
    if (ji >= 0) {
      converted += QWERTY[ji]
      changed = true
      continue
    }
    converted += ch
  }
  return changed ? converted : ''
}

/** Все правдоподобные интерпретации запроса. */
export function queryVariants(raw: string): string[] {
  const base = normText(raw)
  if (!base) return []
  const variants = new Set<string>([base, latinToCyrillic(base), cyrillicToLatin(base)])
  const layout = fixKeyboardLayout(base)
  if (layout) {
    variants.add(layout)
    variants.add(latinToCyrillic(layout))
  }
  return [...variants].filter(Boolean)
}

/** Дамерау-Левенштейн с потолком (производительность). */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: канонический алгоритм, дробление ухудшит читаемость
export function editDistance(a: string, b: string, max = 2): number {
  if (Math.abs(a.length - b.length) > max) return max + 1
  let prev: number[] = Array.from({ length: b.length + 1 }, (_, i) => i)
  let prevPrev: number[] = []
  for (let i = 1; i <= a.length; i++) {
    const cur = [i]
    let best = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      let v = Math.min(
        (prev[j] as number) + 1,
        (cur[j - 1] as number) + 1,
        (prev[j - 1] as number) + cost,
      )
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, (prevPrev[j - 2] as number) + 1)
      }
      cur[j] = v
      if (v < best) best = v
    }
    if (best > max) return max + 1
    prevPrev = prev
    prev = cur
  }
  return prev[b.length] as number
}

/** Скоринг иглы против одного ключевого слова; 0 — не матч. */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: каскад правил скоринга — единый список приоритетов
export function scoreKeyword(needle: string, keyword: string): number {
  if (!keyword) return 0
  if (keyword === needle) return 120
  if (keyword.startsWith(needle)) return 100 - Math.min(20, keyword.length - needle.length)
  if (keyword.split(' ').some((w) => w.startsWith(needle))) return 84
  if (keyword.includes(needle)) return 66
  if (needle.length >= 4) {
    for (const word of keyword.split(' ')) {
      if (Math.abs(word.length - needle.length) <= 2) {
        const d = editDistance(needle, word, needle.length >= 6 ? 2 : 1)
        if (d === 1) return 58
        if (d === 2) return 44
      }
    }
  }
  // Подпоследовательность (аббревиатура): «мстр» → «мастерстрой»
  if (needle.length >= 3) {
    let i = 0
    for (const ch of keyword) {
      if (ch === needle[i]) i++
      if (i === needle.length) return 34
    }
  }
  return 0
}
